/**
 * Shared offline persistence DB (`shadygram-offline`). One IndexedDB for all
 * offline-first state so the manual-logout wipe has a single target:
 *   kv          — small key/value records (persisted CryptoKeys, lastUserId)
 *   conv-meta   — conversation metadata snapshot (single record)
 *   conv-bodies — full conversation bodies, LRU-bounded by lastActivityAt
 *   outbox      — durable queue of writes made while offline (see outbox.ts)
 *
 * All operations are best-effort and never throw: storage failure degrades
 * to online-only behavior, surfaced via the `offline:storage-error` window
 * event (mirrors the yjs pendingAuthQueue pattern) — it must never block UI.
 */

const DB_NAME = 'shadygram-offline';
const DB_VERSION = 1;

export const KV_STORE = 'kv';
export const CONV_META_STORE = 'conv-meta';
export const CONV_BODIES_STORE = 'conv-bodies';
export const OUTBOX_STORE = 'outbox';

let dbPromise: Promise<IDBDatabase | null> | null = null;

export function openOfflineDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(KV_STORE)) {
          db.createObjectStore(KV_STORE, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(CONV_META_STORE)) {
          db.createObjectStore(CONV_META_STORE, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(CONV_BODIES_STORE)) {
          const bodies = db.createObjectStore(CONV_BODIES_STORE, { keyPath: 'id' });
          bodies.createIndex('lastActivityAt', 'lastActivityAt');
        }
        if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
          const outbox = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id', autoIncrement: true });
          outbox.createIndex('entityKey', 'entityKey');
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab may run a future upgrade; yield the connection.
        db.onversionchange = () => { db.close(); dbPromise = null; };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

/** Close the shared connection so deleteDatabase isn't blocked (used by wipe). */
export async function closeOfflineDb(): Promise<void> {
  const promise = dbPromise;
  dbPromise = null;
  if (!promise) return;
  try {
    (await promise)?.close();
  } catch { /* already closed */ }
}

function reportStorageError(op: string, error?: DOMException | null): void {
  try {
    window.dispatchEvent(new CustomEvent('offline:storage-error', {
      detail: { op, error: error?.name ?? 'unknown' },
    }));
  } catch { /* SSR */ }
}

export async function idbGet<T = unknown>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openOfflineDb();
  if (!db) return undefined;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(store, 'readonly').objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

export async function idbGetAll<T = unknown>(store: string): Promise<T[]> {
  const db = await openOfflineDb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const req = db.transaction(store, 'readonly').objectStore(store).getAll();
      req.onsuccess = () => resolve((req.result || []) as T[]);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

export async function idbPut(store: string, value: unknown): Promise<boolean> {
  const db = await openOfflineDb();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value as never);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => { reportStorageError(`put:${store}`, tx.error); resolve(false); };
      tx.onabort = () => { reportStorageError(`put:${store}`, tx.error); resolve(false); };
    } catch {
      resolve(false);
    }
  });
}

export async function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  const db = await openOfflineDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

// ── kv convenience ──────────────────────────────────────────────────────────

export async function kvGet<T = unknown>(key: string): Promise<T | undefined> {
  const record = await idbGet<{ key: string; value: T }>(KV_STORE, key);
  return record?.value;
}

export async function kvSet(key: string, value: unknown): Promise<boolean> {
  return idbPut(KV_STORE, { key, value });
}

export async function kvDelete(key: string): Promise<void> {
  return idbDelete(KV_STORE, key);
}

/**
 * Ask the browser to exempt our storage from eviction (iOS PWA especially).
 * Best-effort and idempotent; failure just means default eviction rules.
 */
export function requestPersistentStorage(): void {
  try {
    void navigator.storage?.persist?.();
  } catch { /* best-effort */ }
}
